import apiClient from "../core/apiClient";
import qs from "qs"; // נדרשת לספרייה זו כדי לקודד בצורה מותאמת אישית אם יש צורך
import { FundDataBasic, FundDataYields, FundFacet, FundFileType } from "../interfaces/DTO/FundDataBasic";
import { FacetType, FundFacetItem } from "../components/GemelData/MiniGemelDataFacetsList";
import { resetHandlerCodeMapCache } from "../utils/namesFormater";
import { LOCAL_STORAGE_KEYS, HANDLER_CODE_MAP_TTL_MS } from "./local_storage_keys";
import { GemelDataFilteredView } from "../components/GemelData/types";
import { ProductType } from "../interfaces/DTO/mislaka_xml_models";

const FINANCIAL_DATA_PREFIX = "/financial-data";
export type HandlerEntry = {
  code: string;
  name: string;
  isGeneral: boolean;
};

class FundService {
  private fundIdMap: Map<number, FundDataBasic> | null = null;

  private buildFundIdMap(): Map<number, FundDataBasic> {
    if (this.fundIdMap) return this.fundIdMap;
    this.fundIdMap = new Map();
    for (const fileType of [FundFileType.GEMELNET, FundFileType.PENSIANET, FundFileType.INSURANCENET]) {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.fundsData(fileType));
      if (!raw) continue;
      try {
        const funds = JSON.parse(raw) as FundDataBasic[];
        for (const fund of funds) {
          this.fundIdMap.set(fund.fund_id, fund);
        }
      } catch {
        console.warn(`⚠️ נתונים פגומים ב־LocalStorage עבור ${fileType}`);
      }
    }
    return this.fundIdMap;
  }

  invalidateFundIdMap() {
    this.fundIdMap = null;
  }

  /**
   * שליפת כל נתוני הקרנות.
   * @returns רשימת נתוני קרנות.
   */
  async fetchAllFunds(): Promise<FundDataBasic[]> {
    const response = await apiClient.get<FundDataBasic[]>(`${FINANCIAL_DATA_PREFIX}/top-yields?top_n=10`);
    return response.data;
  }

  /**
   * שליפת קרנות לפי סוג קובץ עם תמיכה ב־ETag ו־LocalStorage.
   * @param fileType סוג הקובץ (GEMELNET, PENSIANET, INSURANCENET)
   * @returns רשימת הקרנות
   */
  async fetchFundsByFileTypeWithCache(fileType: FundFileType): Promise<FundDataBasic[]> {
    const dataKey = LOCAL_STORAGE_KEYS.fundsData(fileType);
    const etagKey = LOCAL_STORAGE_KEYS.fundsEtag(fileType);
  
    const cached = localStorage.getItem(dataKey);
    const cachedEtag = localStorage.getItem(etagKey);
  
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        this.updateFundsIfChanged(fileType, cachedEtag, dataKey, etagKey);
        return parsed;
      } catch {
        // fallback
      }
    }
  
    const res = await apiClient.get<FundDataBasic[]>(
      `${FINANCIAL_DATA_PREFIX}/funds/by-type`,
      {
        params: { file_type: fileType },
        headers: cachedEtag ? { "If-None-Match": cachedEtag } : {},
        validateStatus: (status) => [200, 304].includes(status),
      }
    );
  
    if (res.status === 200) {
      const etag = res.headers["etag"];
      if (etag) {
        localStorage.setItem(etagKey, etag);
      }
  
      localStorage.setItem(dataKey, JSON.stringify(res.data));
      this.invalidateFundIdMap();
      this.buildHandlerCodeMapFromAllCachedFunds();

      return res.data;
    } else if (res.status === 304 && cached) {
      return JSON.parse(cached);
    } else {
      throw new Error("Failed to fetch funds");
    }
  }
  


  /** בקשת עדכון ברקע אם יש שינוי */
  private async updateFundsIfChanged(
    fileType: FundFileType,
    etag: string | null,
    dataKey: string,
    etagKey: string
  ) {
    try {
      const res = await apiClient.get<FundDataBasic[]>(
        `${FINANCIAL_DATA_PREFIX}/funds/by-type`,
        {
          params: { file_type: fileType },
          headers: etag ? { "If-None-Match": etag } : {},
          validateStatus: (status) => [200, 304].includes(status),
        }
      );
  
      if (res.status === 200) {
        localStorage.setItem(dataKey, JSON.stringify(res.data));
        this.invalidateFundIdMap();
        const newEtag = res.headers["etag"];
        if (newEtag) {
          localStorage.setItem(etagKey, newEtag);
        }

        this.buildHandlerCodeMapFromAllCachedFunds();
      }
    } catch (err) {
      console.warn(`Background update for ${fileType} failed:`, err);
    }
  }
  
  private buildHandlerCodeMapFromAllCachedFunds = () => {
    const lastUpdatedStr = localStorage.getItem(LOCAL_STORAGE_KEYS.handlerCodeMapUpdatedAt);
    const lastUpdated = lastUpdatedStr ? parseInt(lastUpdatedStr) : 0;
    const now = Date.now();
  
    if (now - lastUpdated < HANDLER_CODE_MAP_TTL_MS) {
      console.info("Skipped handler_code_map rebuild — recently updated");
      return;
    }
  
    const fileTypes: FundFileType[] = [
      FundFileType.GEMELNET,
      FundFileType.PENSIANET,
      FundFileType.INSURANCENET,
    ];
  
    const allFunds: FundDataBasic[] = [];
  
    for (const fileType of fileTypes) {
      const dataKey = LOCAL_STORAGE_KEYS.fundsData(fileType);
      const raw = localStorage.getItem(dataKey);
      if (!raw) continue;
  
      try {
        const parsed = JSON.parse(raw) as FundDataBasic[];
        allFunds.push(...parsed);
      } catch {
        console.warn(`Invalid cached data for ${fileType}`);
      }
    }
  
    const handlerEntries = this.createHandlerCodeMap(allFunds);
    localStorage.setItem(LOCAL_STORAGE_KEYS.handlerCodeMap, JSON.stringify(handlerEntries));
    localStorage.setItem(LOCAL_STORAGE_KEYS.handlerCodeMapUpdatedAt, now.toString());
    resetHandlerCodeMapCache(); // ← נאפס גם מהזיכרון
    console.info("handler_code_map rebuilt ✅");
  };
  
private createHandlerCodeMap = (funds: FundDataBasic[]): HandlerEntry[] => {
  const seen = new Set<string>();
  const entries: HandlerEntry[] = [];

  for (const fund of funds) {
    const code = String(fund.managing_corporation_legal_id);
    const name = fund.managing_corporation;

    if (!code || !name || seen.has(code)) continue;

    const isGeneral = !fund.target_population || fund.target_population === "";
    seen.add(code);
    entries.push({ code, name, isGeneral });
  }

  entries.sort((a, b) => {
    if (a.isGeneral !== b.isGeneral) {
      return a.isGeneral ? -1 : 1; // כלליים לפני פרטיים
    }
    return a.name.localeCompare(b.name); // מיון אלפביתי בתוך כל קבוצה
  });

  return entries;
};

  /**
   * שליפת פרטי מסלול השקעה לפי קוד מסלול.
   * @param pathCode קוד המסלול.
   * @returns רשימת נתוני מסלולי השקעה.
   */
  async fetchFundById(fund_id: number): Promise<FundDataBasic[]> {
    const response = await apiClient.get<FundDataBasic[]>(`${FINANCIAL_DATA_PREFIX}/fund/${fund_id}`);
    return response.data;
  }

  /**
 * שליפת נתוני קרנות לפי מערך של fund_id.
 * @param fundIds מערך של קודי קרנות.
 * @returns רשימת נתוני קרנות.
 */
//  async fetchFundsByIds(fundIds: number[]): Promise<FundDataBasic[]> {
//     try {
//       const response = await apiClient.get<FundDataBasic[]>(`${FINANCIAL_DATA_PREFIX}/funds`, {
//         // קידוד פרמטרים באופן ידני במידת הצורך
//         params: { fund_ids: fundIds.join(",") },
//         paramsSerializer: (params) => qs.stringify(params, { encode: true }), // שימוש ב-qs לאימות קידוד
//       });
//       return response.data;
//     } catch (error) {
//       console.error(`Failed to fetch funds by IDs: ${fundIds}`, error);
//       throw new Error("Error fetching funds by IDs");
//     }
//   }
  getFundFromCacheById(fundId: number): FundDataBasic | undefined {
    return this.buildFundIdMap().get(fundId);
  }
  getFundsFromCacheByIds(fundIds: number[]): FundDataBasic[] {
    const results: FundDataBasic[] = [];
  
    for (const id of fundIds) {
      const fund = this.getFundFromCacheById(id);
      if (fund) {
        results.push(fund);
      }
    }
  
    return results;
  }
  /**
   * שליפת תשואות קרנות לפי מזהי קרנות וטווח חודשים.
   * @param fundIds מערך של מזהי קרנות.
   * @param monthsBack מספר חודשים אחורה לחיפוש (אופציונלי, ברירת מחדל 12).
   * @returns רשימת נתוני תשואות קרנות.
   */
  async fetchFundsYieldsByIds(fundIds: number[], monthsBack: number = 12): Promise<FundDataYields[]> {
    try {
      const response = await apiClient.get<FundDataYields[]>(`${FINANCIAL_DATA_PREFIX}/funds/yields`, {
        // קידוד פרמטרים באופן מותאם
        params: { 
          fund_ids: fundIds.join(","), 
          months_back: monthsBack 
        },
        paramsSerializer: (params) => qs.stringify(params, { encode: true }),
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch fund yields by IDs: ${fundIds}`, error);
      throw new Error("Error fetching fund yields by IDs");
    }
  }
  async fetchFundsCustomFieldsByIds(
    fundIds: number[],
    fields: string[],
    monthsBack: number = 12
  ): Promise<Record<string, any>[]> {
    try {
      const response = await apiClient.get<Record<string, any>[]>(
        `${FINANCIAL_DATA_PREFIX}/funds/custom-fields`,
        {
          params: {
            fund_ids: fundIds.join(","),
            fields: fields.join(","),
            months_back: monthsBack,
          },
          paramsSerializer: (params) => qs.stringify(params, { encode: true }),
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        `Failed to fetch custom fields for fund IDs: ${fundIds} and fields: ${fields}`,
        error
      );
     
    }
  }
  
  async uploadCsv(file: File, fileType: FundFileType): Promise<{ detail: any }> {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("file_type", fileType);

      const response = await apiClient.post<{ detail: any }>(
        `${FINANCIAL_DATA_PREFIX}/upload-csv`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error(`Failed to upload CSV file`, error);
      throw new Error("Error uploading CSV file");
    }
  }

  /**
   * שליפת נתוני קרנות מה-API החיצוני (data.gov.il) ועיבודם באותו תהליך כמו קובץ CSV.
   * @param fileType סוג הקובץ (נתמכים: GEMELNET, PENSIANET, INSURANCENET)
   * @param reportPeriod תקופת דיווח בפורמט YYYYMM (אופציונלי, ברירת מחדל: החודש הקודם)
   */
  async fetchFromApi(
    fileType: FundFileType,
    reportPeriod?: number
  ): Promise<{ status: string; detail: any; report_period?: number }> {
    const formData = new FormData();
    formData.append("file_type", fileType);
    if (reportPeriod !== undefined) {
      formData.append("report_period", String(reportPeriod));
    }

    const response = await apiClient.post<{ status: string; detail: any; report_period?: number }>(
      `${FINANCIAL_DATA_PREFIX}/fetch-from-api`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  }
getFundsFromCacheByCriteria(criteria: {
  productType?: number;
  sub_specialization?: string;
  handler_code?: string;
  facets?: FundFacetItem[]; // ← הרחבה חדשה
   includeIRA?: boolean; // ← חדש
   forGeneralPublic?: boolean; // ← true = רק לציבור הרחב (target_population ריק), false = הכל
}): FundDataBasic[] {
  const fileTypes = [
    FundFileType.GEMELNET,
    FundFileType.PENSIANET,
    FundFileType.INSURANCENET,
  ];

  const results: FundDataBasic[] = [];

  for (const fileType of fileTypes) {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.fundsData(fileType));
    if (!raw) continue;

    try {
      const funds = JSON.parse(raw) as FundDataBasic[];

      const filtered = funds.filter((fund) => {
        const matchesProductType =
          !criteria.productType ||
          fund.product_type === criteria.productType ||
          (fund.product_type === ProductType.LIFE_INSURANCE_AND_PURE_SAVINGS &&
            (criteria.productType === ProductType.LIFE_INSURANCE_SAVINGS ||
              criteria.productType === ProductType.PURE_SAVINGS_POLICY));

        const matchesSubSpecialization =
          !criteria.sub_specialization ||
          fund.sub_specialization === criteria.sub_specialization;

        const matchesHandlerCode =
          !criteria.handler_code ||
          String(fund.managing_corporation_legal_id) === criteria.handler_code;

        const matchesFacets =
          !criteria.facets ||
          criteria.facets.every((facet) => {
            switch (facet.facetType) {
              case FacetType.FundClassification:
                return fund.fund_classification === facet.facetValue;
              case FacetType.SubSpecialization:
                return fund.sub_specialization === facet.facetValue;
              case FacetType.ManagingCorporation:
                return (
                  String(fund.managing_corporation_legal_id) === String(facet.facetValue)
                );
              case FacetType.FundName:
                return fund.fund_id === facet.facetValue;
              default:
                return true;
            }
          });

        const excludeIRA =
          criteria.includeIRA === false &&
          fund.sub_specialization === "בניהול אישי IRA";

        // פילטר לציבור הרחב: אם forGeneralPublic=true, רק קרנות עם target_population ריק
        const matchesTargetPopulation =
          criteria.forGeneralPublic === undefined ||
          criteria.forGeneralPublic === false ||
          !fund.target_population ||
          fund.target_population === "";

        return (
          matchesProductType &&
          matchesSubSpecialization &&
          matchesHandlerCode &&
          matchesFacets &&
          matchesTargetPopulation &&
                 !excludeIRA // ← החידוש כאן
        );
      });

      results.push(...filtered);
    } catch {
      console.warn(`Invalid cached data for ${fileType}`);
    }
  }

  return results;
}

   /**
 * שליפת הקרנות המצטיינות לפי קריטריונים.
 * @param filterField שם השדה לסינון.
 * @param filterValue הערך לסינון.
 * @param reportPeriod תקופת הדיווח. (אופציונלי)
 * @param sortField שם השדה המספרי למיון.
 * @param topN מספר התוצאות הרצויות.
 * @returns רשימת הקרנות המצטיינות.
 */
async fetchTopFundsByCriteria(
  filterField: FacetType,
  filterValue: string,
  reportPeriod?: string,
  sortField: string = "year_to_date_yield",
  topN: number = 10
): Promise<FundDataBasic[]> {
  const queryParams = new URLSearchParams({
    filter_field: filterField,
    filter_value: filterValue,
    sort_field: sortField,
    top_n: topN.toString(),
  });

  if (reportPeriod) {
    queryParams.append("report_period", reportPeriod);
  }

  const response = await apiClient.get<FundDataBasic[]>(
    `${FINANCIAL_DATA_PREFIX}/funds/top?${queryParams.toString()}`
  );
  return response.data;
}

/**
 * מסנן קרנות לפי GemelDataFilteredView
 * לוגיקה: בין שדות שונים - AND, בתוך מערך של שדה - OR
 * @param filters אובייקט פילטרים מסוג GemelDataFilteredView
 * @returns מערך של fund_id מסוננים
 */
getFilteredFundIds(filters: GemelDataFilteredView): number[] {
  // קבלת כל הקרנות לפי productType (ללא IRA)
  const allFunds = this.getFundsFromCacheByCriteria({
    productType: filters.productType,
    includeIRA: false, // ללא קרנות IRA
    forGeneralPublic: filters.forGeneralPublic, // סינון לפי אוכלוסיית יעד
  });

  const filtered = allFunds.filter((fund) => {
    // פילטר handlers (OR בין האברים)
    const matchesHandler =
      filters.handlers.length === 0 ||
      filters.handlers.some(
        (handlerCode) =>
          String(fund.managing_corporation_legal_id) === handlerCode
      );

    // פילטר subSpecializations (OR בין האברים)
    const matchesSubSpecialization =
      filters.subSpecializations.length === 0 ||
      filters.subSpecializations.some(
        (subSpec) => fund.sub_specialization === subSpec
      );

    // פילטר stockExposureRanges (OR בין האברים)
    const matchesStockExposure =
      filters.stockExposureRanges.length === 0 ||
      filters.stockExposureRanges.some((range) => {
        const stockExposure = fund.stock_market_percent ?? 0;
        return this.isInExposureRange(stockExposure, range);
      });

    // פילטר foreignExposureRanges (OR בין האברים)
    const matchesForeignExposure =
      filters.foreignExposureRanges.length === 0 ||
      filters.foreignExposureRanges.some((range) => {
        const foreignExposure = fund.foreign_exposure_percent ?? 0;
        return this.isInExposureRange(foreignExposure, range);
      });

    // פילטר currencyExposureRanges (OR בין האברים)
    const matchesCurrencyExposure =
      filters.currencyExposureRanges.length === 0 ||
      filters.currencyExposureRanges.some((range) => {
        const currencyExposure = fund.foreign_currency_exposure_percent ?? 0;
        return this.isInExposureRange(currencyExposure, range);
      });

    // AND בין כל הפילטרים
    return (
      matchesHandler &&
      matchesSubSpecialization &&
      matchesStockExposure &&
      matchesForeignExposure &&
      matchesCurrencyExposure
    );
  });

  return filtered.map((fund) => fund.fund_id);
}

/**
 * בדיקה האם ערך נמצא בטווח חשיפה
 * @param value הערך לבדוק
 * @param range הטווח (למשל "0-10", "10-30", וכו')
 * @returns true אם הערך בטווח
 */
private isInExposureRange(value: number, range: string): boolean {
  // ייבוא stockExposureBuckets מקובץ fundsFormatter
  const { stockExposureBuckets } = require('../utils/fundsFormatter');

  // חיפוש הבאקט המתאים לפי label (לחשיפה למניות בלבד)
  const bucket = stockExposureBuckets.find((b: any) => b.label === range);

  if (bucket) {
    // שימוש בערכי min ו-max המדויקים מה-bucket
    return value >= bucket.min && value < bucket.max;
  }

  // fallback - לחשיפה לחו"ל ולמט"ח או טווחים אחרים
  const [min, max] = range.split("-").map(Number);

  // טיפול מיוחד לטווחים שמסתיימים ב-100: להכליל גם ערכים מעל 100
  if (max === 100) {
    return value >= min && value <= 130; // עד 130% כמו בחשיפה למניות
  }

  return value >= min && value < max;
}

/**
 * שליפת report_period מקרן ספציפית בגמלנט (8563)
 * @returns תאריך תקופת הדיווח או null
 */
getReportPeriodFromGemelnet(): string | null {
  const REFERENCE_FUND_ID = 8563;
  const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.fundsData(FundFileType.GEMELNET));

  if (!raw) return null;

  try {
    const funds = JSON.parse(raw) as FundDataBasic[];
    const fund = funds.find((f) => f.fund_id === REFERENCE_FUND_ID);
    return fund?.report_period || null;
  } catch {
    console.warn(`⚠️ נתונים פגומים ב־LocalStorage עבור GEMELNET`);
    return null;
  }
}


}

export const fundService = new FundService();
